import { DocumentOption } from './DocumentOption';
import { RepositoryDocument, RepositoryDocumentPage } from './types';

interface Props {
    listId: string;
    query: string;
    page: RepositoryDocumentPage | undefined;
    failed: boolean;
    optionId: (document: RepositoryDocument) => string;
    activeIndex: number;
    isAdded: (document: RepositoryDocument) => boolean;
    onPick: (document: RepositoryDocument) => void;
    onHover: (index: number) => void;
}

function headerLabel(query: string, total: number): string {
    if (query.trim()) return `${total} ${total === 1 ? 'result' : 'results'}`;
    return `Documents (${total})`;
}

function Message({ title, hint }: { title: string; hint?: string }) {
    return (
        <div className="flex flex-col gap-1 px-4 py-6">
            <span className="text-sm font-semibold">{title}</span>
            {hint && (
                <span className="text-[13px] text-muted-foreground">{hint}</span>
            )}
        </div>
    );
}

function PanelBody(props: Props) {
    const { page, failed, query } = props;
    if (!page) {
        return failed ? (
            <Message title="Couldn't load documents. Try again." />
        ) : (
            <Message title="Searching…" />
        );
    }
    if (page.results.length === 0) {
        if (!query.trim()) {
            return <Message title="No documents in the repository yet." />;
        }
        return (
            <Message
                title={`No documents match “${query.trim()}”`}
                hint="Search by full or partial reference, such as AD-2024, or by words in the title."
            />
        );
    }
    return (
        <div
            id={props.listId}
            role="listbox"
            aria-label="Documents"
            className="max-h-[336px] overflow-y-auto p-1"
        >
            {page.results.map((document, index) => (
                <DocumentOption
                    key={document.id}
                    id={props.optionId(document)}
                    document={document}
                    query={query}
                    active={index === props.activeIndex}
                    added={props.isAdded(document)}
                    onPick={() => props.onPick(document)}
                    onHover={() => props.onHover(index)}
                />
            ))}
        </div>
    );
}

/** The popover under the field: header, options, empty or error state, and the truncation footer. */
export function DocumentResultsPanel(props: Props) {
    const { page, query } = props;
    return (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-20 flex flex-col overflow-hidden rounded-[10px] border bg-white shadow-lg">
            {page && page.results.length > 0 && (
                <div className="border-b px-4 py-3 text-xs font-semibold text-muted-foreground">
                    {headerLabel(query, page.total)}
                </div>
            )}
            <PanelBody {...props} />
            {page && page.total > page.results.length && (
                <div className="border-t bg-slate-50 px-4 py-2 text-xs text-muted-foreground">
                    {`Showing ${page.results.length} of ${page.total}. Type more of the reference or title to narrow the list.`}
                </div>
            )}
        </div>
    );
}
